<script lang="ts">
  import { onMount } from 'svelte';
  import { goto } from '$app/navigation';
  import { resolve } from '$app/paths';
  import { page } from '$app/state';
  import type { SessionInfo } from '#src/generated/protocol';
  import { runtimeConfig } from '#lib/config/runtime-config.js';
  import { useCoreClient } from '#lib/core/context.js';
  import { defaultSettingsSection } from '#lib/features/settings/settings-navigation.js';
  import { pushOverride } from '#lib/notifications/push-config.js';
  import { logoutWithPush } from '#lib/features/notifications/web-push.js';
  import { i18n } from '#lib/i18n.js';
  import { UserProfile } from '#lib/profile/user-profile.svelte.js';
  import ActionMenu from '#lib/ui/primitives/ActionMenu.svelte';
  import Avatar from '#lib/ui/primitives/Avatar.svelte';
  import Button from '#lib/ui/primitives/Button.svelte';
  import DialogActions from '#lib/ui/primitives/DialogActions.svelte';
  import DialogFrame from '#lib/ui/primitives/DialogFrame.svelte';
  import { usePresenceStore } from '#lib/rooms/presence.svelte.js';
  import PresenceDot from '#lib/ui/primitives/PresenceDot.svelte';
  import { preferences } from '#lib/settings/preferences.svelte.js';
  import { resolveUserStatus } from '#lib/rooms/user-status.js';
  import ProfileCard from '#lib/ui/primitives/ProfileCard.svelte';
  import Tooltip from '#lib/ui/primitives/Tooltip.svelte';
  import AccountMenuItems from './AccountMenuItems.svelte';
  import { AccountDirectory } from './account-directory.svelte.js';
  import { SignOutGuard } from './sign-out-guard.svelte.js';
  import SignOutWarningDialog from './SignOutWarningDialog.svelte';
  import '#lib/ui/primitives/nav-tab.css';
  import './sidebar-tools.css';

  type Mode = 'mobile' | 'compact' | 'desktop';

  interface Props {
    mode: Mode;
  }

  let { mode }: Props = $props();
  const core = useCoreClient();
  const presenceStore = usePresenceStore();
  const accountProfiles = new AccountDirectory(core);
  const signOut = new SignOutGuard(core);
  let switching = $state(false);
  let accountSwitching = $state(true);
  let logoutAccountId = $state<string | null>(null);
  let accountToLogout = $derived(
    core.accounts.find((account) => account.account_id === logoutAccountId) ?? null
  );
  const ownProfile = new UserProfile(core, () => core.session?.user_id);
  let activeProfile = $derived(ownProfile.current);
  let displayName = $derived(activeProfile?.display_name ?? core.session?.user_id ?? '?');
  let userStatus = $derived(
    resolveUserStatus(activeProfile, presenceStore.get(core.session?.user_id ?? ''))
  );
  let avatarUrl = $derived(activeProfile?.avatar_url ?? null);
  let ownPresence = $derived(preferences.sendPresence ? preferences.presence : null);

  onMount(() => {
    void runtimeConfig().then((config) => {
      accountSwitching = !config.disableAccountSwitcher;
    });
  });

  async function switchAccount(accountId: string): Promise<void> {
    if (accountId === core.session?.account_id || switching) return;
    switching = true;
    try {
      await core.switchAccount(accountId);
      await goto(resolve('/(app)/rooms'));
    } finally {
      switching = false;
    }
  }

  function openAddAccount(): void {
    void goto(resolve('login?addAccount=1'));
  }

  function reauthenticate(account: SessionInfo): void {
    void goto(
      resolve(
        `login?addAccount=1&reauth=${encodeURIComponent(account.account_id)}&server=${encodeURIComponent(account.homeserver)}`
      )
    );
  }

  function openProfile(): void {
    const section = defaultSettingsSection();
    if (mode === 'mobile') {
      void goto(resolve(`settings/${section}`));
      return;
    }

    void goto(resolve(`settings/${section}`), { shallow: true, state: { settings: { section } } });
  }

  async function logoutAccount(accountId: string): Promise<void> {
    if (switching) return;

    switching = true;
    try {
      if (accountId !== core.session?.account_id) {
        const account = core.accounts.find((account) => account.account_id === accountId);
        if (account?.needs_reauth) {
          await core.removeAccount(accountId);
          return;
        }
        await core.switchAccount(accountId);
      }
      await signOut.request(async () => {
        await logoutWithPush(core, pushOverride());
        if (core.status === 'ready') await goto(resolve('/(app)/rooms'));
      }, true);
    } finally {
      switching = false;
    }
  }

  function requestLogout(accountId: string): void {
    logoutAccountId = accountId;
  }

  function logout(): void {
    void signOut.request(() => logoutWithPush(core, pushOverride()));
  }

  function openStatusEdit(): void {
    void goto(resolve('settings/account?editStatus'), {
      shallow: true,
      state: { settings: { section: 'account' } },
    });
  }
</script>

{#snippet ownAvatar()}
  <Avatar size="small" src={avatarUrl} name={displayName} alt={displayName} refreshOnResume />
  {#if ownPresence}
    <PresenceDot
      presence={ownPresence}
      label={i18n.t(`presence.${ownPresence}`)}
      class="account-presence"
      size="medium"
    />
  {/if}
{/snippet}

{#if mode === 'mobile'}
  <a
    class="quick-tool mobile-tool account-tool selection-layer"
    href={resolve('/(app)/profile')}
    aria-label={i18n.t('nav.manageAccounts')}
    aria-current={page.url.pathname === '/profile' ? 'page' : undefined}
    draggable="false"
  >
    {@render ownAvatar()}
  </a>
{:else}
  {#snippet profileTrigger({ props: tooltipProps }: { props: Record<string, unknown> })}
    <ActionMenu
      label={i18n.t('nav.switchAccount')}
      class="sidebar-popover"
      side={mode === 'compact' ? 'right' : 'top'}
      align={mode === 'compact' ? 'center' : 'start'}
      sideOffset={mode === 'compact' ? 20 : 24}
    >
      {#snippet trigger({ props })}
        <button
          {...tooltipProps}
          {...props}
          type="button"
          class="quick-tool nav-tab nav-tab-outlined selection-open selection-layer {mode ===
          'compact'
            ? 'compact-tool nav-tab-side'
            : 'desktop-tool nav-tab-bottom'}"
          aria-label={i18n.t('nav.switchAccount')}
        >
          <span class="nav-tab-hit-area" aria-hidden="true"></span>
          {@render ownAvatar()}
        </button>
      {/snippet}
      <ProfileCard
        class="account-profile-header"
        {displayName}
        userId={core.session?.user_id ?? ''}
        {avatarUrl}
        color={activeProfile?.hero_color ?? 'var(--primary-container)'}
        heroColor={activeProfile?.hero_color}
        heroBrightness={activeProfile?.hero_brightness}
        bannerUrl={activeProfile?.banner_url}
        status={userStatus?.text}
        statusEmoji={userStatus?.emoji}
        nameColorLight={activeProfile?.name_color_light}
        nameColorDark={activeProfile?.name_color_dark}
      >
        {#snippet statusField()}
          <button
            type="button"
            class="account-profile-status-button"
            onclick={openStatusEdit}
            aria-label={i18n.t('nav.editStatus')}
          >
            <p class="account-profile-status-text explicit-scrollbar" role="region">
              {#if userStatus?.emoji}<span class="account-profile-status-emoji"
                  >{userStatus?.emoji}</span
                >{/if}
              {#if userStatus?.text}{userStatus?.text}{:else}<span
                  class="account-profile-status-placeholder"
                  >{i18n.t('common.statusPlaceholder')}</span
                >{/if}
            </p>
          </button>
        {/snippet}
      </ProfileCard>
      <AccountMenuItems
        accounts={core.accounts}
        profiles={accountProfiles}
        currentAccountId={core.session?.account_id}
        {switching}
        onSwitch={switchAccount}
        onProfile={openProfile}
        onLogoutAccount={requestLogout}
        onReauth={reauthenticate}
        onLogout={logout}
        onAddAccount={openAddAccount}
        {accountSwitching}
      />
    </ActionMenu>
  {/snippet}
  <Tooltip
    label={i18n.t('nav.switchAccount')}
    side={mode === 'compact' ? 'right' : 'top'}
    align={mode === 'compact' ? 'center' : 'start'}
    trigger={profileTrigger}
  />
{/if}

<DialogFrame
  open={accountToLogout !== null}
  variant="verification"
  label={i18n.t('settings.logout')}
  onOpenChange={(open) => {
    if (!open && !switching) logoutAccountId = null;
  }}
>
  {#if accountToLogout}
    <div class="logout-dialog">
      <h2>{i18n.t('settings.logout')}</h2>
      <p>{accountToLogout.user_id}</p>
      <DialogActions>
        <Button variant="ghost" disabled={switching} onclick={() => (logoutAccountId = null)}
          >{i18n.t('common.cancel')}</Button
        >
        <Button
          variant="danger"
          loading={switching}
          onclick={() => {
            const accountId = accountToLogout?.account_id;
            if (accountId) void logoutAccount(accountId).then(() => (logoutAccountId = null));
          }}>{i18n.t('settings.logout')}</Button
        >
      </DialogActions>
    </div>
  {/if}
</DialogFrame>

<SignOutWarningDialog guard={signOut} />

<style>
  .account-tool {
    position: relative;
  }

  :global(.sidebar-popover .profile-card:not([style*='--profile-hero']) .profile-card-identity) {
    padding-bottom: var(--space-150);
  }

  :global(.quick-tool > .account-presence) {
    bottom: -0.125rem;
    position: absolute;
    right: -0.125rem;
  }

  .logout-dialog {
    display: grid;
    gap: var(--space-300);
  }

  .logout-dialog h2,
  .logout-dialog p {
    margin: 0;
  }

  .account-profile-status-text {
    -webkit-box-orient: vertical;
    box-orient: vertical;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    margin: 0;
    max-height: var(--space-1000);
    overflow: hidden;
    overflow-wrap: anywhere;
    overscroll-behavior: contain;
    padding: 0;
  }

  :global(.account-profile-header .profile-card-status) {
    padding: 0;
  }

  :global(.account-profile-header .profile-card-status):has(.account-profile-status-button:hover) {
    filter: brightness(1.1);
    translate: 0 -1px;
  }

  @media (prefers-reduced-motion: no-preference) {
    :global(html:not([data-reduced-motion='on']) .account-profile-header .profile-card-status) {
      transition:
        filter var(--motion-normal) var(--motion-easing-standard),
        translate var(--motion-fast) var(--ease-smooth-out);
    }
  }

  .account-profile-status-button {
    background: none;
    border: 1px transparent solid;
    border-radius: var(--radius);
    color: inherit;
    cursor: pointer;
    display: inline-block;
    min-width: var(--space-700);
    padding: var(--space-200);
    text-align: start;
  }

  .account-profile-status-text:focus-visible {
    outline: var(--focus-ring-width) solid var(--focus-ring);
    outline-offset: calc(-1 * var(--focus-ring-width));
  }

  .account-profile-status-placeholder {
    opacity: 0.5;
  }

  :global(.account-profile-header .profile-card-status:hover .account-profile-status-text),
  :global(.account-profile-header .profile-card-status:focus-within .account-profile-status-text) {
    display: block;
    -webkit-line-clamp: unset;
    line-clamp: unset;
    overflow-y: auto;
  }
</style>
